/**
 * TransactionCalendar 컴포넌트
 *
 * 월별 거래 달력을 표시
 * - date-fns를 사용한 날짜 계산
 * - 7열 그리드 (일요일 ~ 토요일)
 * - CalendarDay 컴포넌트를 활용
 */

import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
} from 'date-fns'
import type { Transaction } from '@/domains/transaction/types/Transaction'
import { CalendarDay } from './CalendarDay'

interface TransactionCalendarProps {
  currentYear: number
  currentMonth: number
  transactions: Transaction[]
  onDateClick: (date: Date, transactions: Transaction[]) => void
}

export function TransactionCalendar({
  currentYear,
  currentMonth,
  transactions,
  onDateClick,
}: TransactionCalendarProps) {
  // 현재 월의 기준 날짜
  const currentDate = new Date(currentYear, currentMonth - 1, 1)

  // 달력에 표시할 날짜 범위 계산 (주의 시작~끝)
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }) // 일요일 시작
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

  // 모든 날짜 배열 생성
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd })

  // 날짜별로 거래 그룹화 (YYYY-MM-DD 형식)
  const transactionsByDate = transactions.reduce(
    (acc, transaction) => {
      const dateKey = transaction.date // 이미 'YYYY-MM-DD' 형식
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(transaction)
      return acc
    },
    {} as Record<string, Transaction[]>
  )

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 mb-2">
        {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
          <div
            key={day}
            className={`text-center text-sm font-semibold py-2 ${
              index === 0
                ? 'text-red-600 dark:text-red-400'
                : index === 6
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 gap-0">
        {calendarDays.map((date) => {
          const dateKey = format(date, 'yyyy-MM-dd')
          const dayTransactions = transactionsByDate[dateKey] || []

          return (
            <CalendarDay
              key={dateKey}
              date={date}
              transactions={dayTransactions}
              isCurrentMonth={isSameMonth(date, currentDate)}
              isToday={isToday(date)}
              onClick={() => onDateClick(date, dayTransactions)}
            />
          )
        })}
      </div>
    </div>
  )
}
